import { useEffect, useRef, useState } from 'react';
import { loadDocument, UnsupportedFileError, type LoadedDocument } from '../lib/document';
import { useT, useLang } from '../i18n';
import { LangSwitcher } from './LangSwitcher';
import { LibraryPicker } from './LibraryPicker';

type Props = {
  onLoaded: (doc: LoadedDocument) => void;
  initialError?: string | null;
  externalLoading?: boolean;
};

export function FilePicker({ onLoaded, initialError, externalLoading }: Props) {
  const t = useT();
  const lang = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const loading = externalLoading || internalLoading;
  const error = internalError ?? initialError ?? null;
  // libraryEnabled default = true (если бэкенд недоступен — показываем кнопку как раньше)
  const [libraryEnabled, setLibraryEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch('/admin/api.php?action=settings', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setLibraryEnabled(d?.libraryEnabled !== false);
      })
      .catch(() => { /* default true */ });
    return () => { cancelled = true; };
  }, []);

  async function handleFile(file: File | undefined, transient = false) {
    if (!file) return;
    setInternalError(null);
    setInternalLoading(true);
    try {
      const doc = await loadDocument(file);
      onLoaded(transient ? { ...doc, transient: true } : doc);
    } catch (e) {
      if (e instanceof UnsupportedFileError) {
        setInternalError(t('picker.error.unsupported'));
      } else {
        setInternalError((e as Error).message);
      }
    } finally {
      setInternalLoading(false);
    }
  }

  function handleLibraryPick(doc: LoadedDocument) {
    setLibraryOpen(false);
    onLoaded(doc);
  }

  return (
    <div className="picker">
      <div className="picker-top">
        <a
          className="games-link"
          href={`https://games.smarty.center/?lang=${lang}`}
          title={t('nav.games')}
        >
          <span aria-hidden="true">🎮</span> {t('nav.games')}
        </a>
        <LangSwitcher />
      </div>
      <div className="picker-card">
        <div className="picker-logo">Draw</div>
        <div className="picker-sub">{t('picker.sub')}</div>
        <div className="picker-actions">
          <button
            className="picker-pick"
            onClick={() => inputRef.current?.click()}
            disabled={loading}
          >
            {loading ? t('picker.loading') : t('picker.pick')}
          </button>
          {libraryEnabled && (
            <button
              className="picker-pick picker-pick-secondary"
              onClick={() => setLibraryOpen(true)}
              disabled={loading}
            >
              {t('picker.library')}
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/*"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {error && <div className="picker-error">{error}</div>}
        <div className="picker-hint">{t('picker.hint')}</div>
      </div>
      {libraryOpen && (
        <LibraryPicker onClose={() => setLibraryOpen(false)} onPick={handleLibraryPick} />
      )}
    </div>
  );
}
